import {
  Dialog,
  DialogContent,
  DialogProps,
  makeStyles,
  Slide,
  SlideProps,
} from '@material-ui/core';
import { IconButton, Typography } from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import cn from 'classnames';
import { CSSProperties, forwardRef, MouseEvent, ReactNode } from 'react';
import { COLORS } from '../../theme/variables';
import { isMobile } from '../../utils/functions';
import Text from './text';

interface DialogTitleProps {
  id?: string;
  children: ReactNode;
  caption?: string;
  onClose: (event: MouseEvent) => any;
  className?: string;
}

interface ModalProps extends DialogProps {
  onClose: (event: MouseEvent) => any;
  width?: string;
  height?: string;
  style?: CSSProperties;
  classNameContent?: string;
  titleProps?: {
    title: ReactNode;
    titleIcon?: ReactNode;
    caption?: string;
    className?: string;
  };
}

const useStyles = makeStyles({
  contentWrapper: {
    width: '100%',
    height: '100%',
    padding: 0,
    boxSizing: 'border-box',
  },
  content: {
    width: '100%',
    height: '100%',
    padding: '32px',
    boxSizing: 'border-box',
  },
});

const useTitleStyles = makeStyles({
  container: {
    padding: '20px 40px 0 32px',
  },
  closeButton: {
    position: 'absolute',
    top: 5,
    right: 8,
  },
  caption: {
    wordBreak: 'break-word',
  },
});

export const DialogTitle = ({
  children,
  onClose,
  caption,
  className,
  ...other
}: DialogTitleProps) => {
  const classes = useTitleStyles();

  return (
    <MuiDialogTitle
      disableTypography
      className={cn(classes.container, className)}
      {...other}>
      <div>
        <Typography variant='h4'>{children}</Typography>
        {!!caption && (
          <Text
            variant='normal'
            color={COLORS.COLOR_TEXT_LIGHTENED_20}
            className={classes.caption}>
            {caption}
          </Text>
        )}
      </div>
      {onClose ? (
        <IconButton
          className={classes.closeButton}
          aria-label='close'
          onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const Transition = forwardRef(function Transition(props: SlideProps, ref) {
  return <Slide direction='left' ref={ref} {...props} />;
});

const ModalV2 = forwardRef<HTMLDivElement, ModalProps>(function ModalV2(
  {
    open,
    onClose,
    className,
    children,
    width,
    height,
    style,
    titleProps,
    classNameContent,
    ...props
  },
  ref,
) {
  const classes = useStyles();

  return (
    <Dialog
      fullScreen={isMobile()}
      TransitionComponent={isMobile() ? Transition : undefined}
      className={className}
      open={open}
      onClose={onClose}
      aria-labelledby='modal-dialog-title'
      PaperProps={{
        elevation: 3,
        style: {
          width: isMobile() ? '100%' : width,
          height,
          maxWidth: isMobile() ? '100%' : width,
          ...style,
        },
      }}
      ref={ref}
      {...props}>
      {titleProps && (
        <DialogTitle
          id='customized-dialog-title'
          caption={titleProps.caption}
          onClose={onClose}
          className={titleProps.className}>
          {titleProps.title}
          {titleProps.titleIcon}
        </DialogTitle>
      )}
      <DialogContent className={classes.contentWrapper}>
        <div className={cn(classes.content, classNameContent)}>{children}</div>
      </DialogContent>
    </Dialog>
  );
});

export default ModalV2;
